import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { buzz, playChime } from "@/lib/sound";

const urlB64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

/** Kirim token ke service worker agar aksi Setujui/Tolak dari notifikasi tetap terautentikasi. */
export const syncSwToken = async () => {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  reg.active?.postMessage({ type: "token", token: localStorage.getItem("ab_token") || "" });
};

/** Daftarkan perangkat ini untuk Web Push (notifikasi walau aplikasi ditutup). */
export const subscribePush = async () => {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("Perangkat tidak mendukung notifikasi push");
  const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Izin notifikasi ditolak di perangkat ini");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.key) }));
  await api.post("/push/subscribe", sub.toJSON());
  await syncSwToken();
  localStorage.setItem("ab_push", "1");
  return true;
};

/** Pendaftaran ulang otomatis tiap app dibuka agar langganan tidak kedaluwarsa. */
export const useAutoPush = () => {
  useEffect(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    subscribePush().catch((e) => console.error("pendaftaran push ulang gagal:", e));
  }, []);
};

/** Banner besar (mudah terlihat di HP) bila izin notifikasi belum diberikan. */
export const NotifBanner = () => {
  const [perm, setPerm] = useState(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (perm === "granted" || perm === "unsupported" || hidden) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      await api.post("/push/test").catch(() => {});
      setPerm("granted");
      toast.success("Notifikasi aktif", { description: "Alarm permintaan saldo akan berbunyi walau aplikasi ditutup." });
    } catch (e) {
      setPerm(Notification.permission);
      toast.error(errMsg(e) || e.message, { description: "Bila terblokir: Setelan HP → Aplikasi → ALPHA BILL → Notifikasi → Izinkan." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="notif-banner"
      className="mb-6 flex flex-col gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
        <div>
          <p className="text-sm font-semibold text-amber-300">Alarm permintaan saldo belum aktif di perangkat ini</p>
          <p className="text-xs text-muted-foreground">
            Aktifkan agar alarm berbunyi keras + getar, dan bisa Setujui/Tolak langsung dari notifikasi HP walau aplikasi ditutup.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button size="sm" disabled={busy} onClick={enable} data-testid="notif-banner-enable" className="rounded-full bg-amber-500 text-black hover:bg-amber-400">
          {busy ? "Mengaktifkan…" : "Aktifkan Sekarang"}
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setHidden(true)} data-testid="notif-banner-dismiss">
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/** Tombol aktifkan notifikasi — izin browser hanya bisa diminta lewat klik pengguna. */
export const NotifEnableButton = () => {
  const [done, setDone] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted" && localStorage.getItem("ab_push") === "1",
  );
  const [busy, setBusy] = useState(false);
  if (done) return null;
  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      await api.post("/push/test").catch(() => {});
      setDone(true);
      toast.success("Notifikasi aktif", { description: "Alarm permintaan saldo akan muncul walau aplikasi ditutup." });
    } catch (e) {
      toast.error(errMsg(e) || e.message, { description: "Buka Setelan HP → Aplikasi → ALPHA BILL → Notifikasi bila terblokir." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm" variant="outline" disabled={busy} onClick={enable} data-testid="enable-notif"
      className="rounded-full border-amber-500/40 text-[11px] text-amber-400 hover:text-amber-300"
    >
      <BellRing className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">{busy ? "Mengaktifkan…" : "Aktifkan Notifikasi"}</span>
    </Button>
  );
};

/** Deteksi browser dalam-aplikasi (WhatsApp/Facebook/Instagram) yang tidak bisa memasang PWA. */
export const inAppBrowser = () => {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "Facebook";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/WhatsApp|; wv\)/i.test(ua) && !/Chrome\/\d+\.\d+\.\d+\.\d+ Mobile Safari/i.test(ua)) return "WhatsApp";
  if (/Line\/|Twitter|TikTok|MicroMessenger/i.test(ua)) return "aplikasi lain";
  return "";
};

export const isStandalone = () =>
  typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone);

/** Registrasi service worker + tombol "Instal Aplikasi" (PWA installable di Android). */
export const useInstallPrompt = () => {
  const [prompt, setPrompt] = useState(typeof window !== "undefined" ? window.__abInstallPrompt : null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    const sync = () => setPrompt(window.__abInstallPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("ab-install-ready", sync);
    window.addEventListener("beforeinstallprompt", sync);
    window.addEventListener("ab-installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) setInstalled(true);
    navigator.getInstalledRelatedApps?.().then((apps) => { if (apps.length) setInstalled(true); }).catch(() => {});
    return () => {
      window.removeEventListener("ab-install-ready", sync);
      window.removeEventListener("beforeinstallprompt", sync);
      window.removeEventListener("ab-installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const p = prompt || window.__abInstallPrompt;
    if (!p) return false;
    p.prompt();
    const res = await p.userChoice?.catch(() => null);
    window.__abInstallPrompt = null;
    setPrompt(null);
    if (res?.outcome === "accepted") setInstalled(true);
    return res?.outcome === "accepted";
  };

  return { prompt, installed, install };
};

export const InstallButton = () => {
  const { prompt, installed, install } = useInstallPrompt();
  const [hidden, setHidden] = useState(localStorage.getItem("ab_install_hide") === "1");
  const nav = useNavigate();
  if (installed || isStandalone() || hidden) return null;
  const click = async () => {
    if (prompt || window.__abInstallPrompt) {
      await install();
      return;
    }
    nav("/instal");
  };
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" className="rounded-full text-[11px]" onClick={click} data-testid="install-app">
        <Download className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Instal Aplikasi</span>
      </Button>
      <Button
        size="icon" variant="ghost" className="h-7 w-7" data-testid="install-dismiss"
        onClick={() => { localStorage.setItem("ab_install_hide", "1"); setHidden(true); }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

/** Alarm permintaan saldo: sirine keras + getar berulang sampai diklik, plus notifikasi sistem. */
export const useSaldoAlarm = () => {
  const timer = useRef(null);

  const stop = () => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    try { navigator.vibrate?.(0); } catch { /* tidak didukung */ }
  };

  const start = async (data) => {
    stop();
    const fire = () => {
      [0, 700, 1400].forEach((ms) => setTimeout(() => playChime("alarm"), ms));
      buzz([700, 200, 700, 200, 700, 200, 1000]);
    };
    fire();
    timer.current = setInterval(fire, 6000);

    if ("Notification" in window && Notification.permission === "granted") {
      try {
        const reg = await navigator.serviceWorker?.ready;
        const body = `Permintaan saldo Rp ${data.amount} dari ${data.username}`;
        const opts = {
          body,
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag: data.deposit_id ? `saldo-${data.deposit_id}` : "saldo",
          renotify: true,
          requireInteraction: true,
          vibrate: [600, 200, 600, 200, 600, 200, 900],
          data: { url: "/mitra", deposit_id: data.deposit_id || "" },
          actions: data.deposit_id
            ? [{ action: "approve", title: "✔ Setujui" }, { action: "reject", title: "✖ Tolak" }]
            : undefined,
        };
        if (reg) await reg.showNotification("ALPHA BILL · PRIORITAS", opts);
        else new Notification("ALPHA BILL · PRIORITAS", opts);
      } catch (e) {
        console.error("notifikasi sistem tidak tersedia:", e);
      }
    }

    toast.error(`PRIORITAS · Permintaan saldo: ${data.username}`, {
      description: `Rp ${data.amount} — ketuk untuk menghentikan alarm`,
      duration: Infinity,
      onDismiss: stop,
      onAutoClose: stop,
      action: { label: "Matikan alarm", onClick: stop },
    });
  };

  useEffect(() => stop, []);
  return { start, stop };
};
